import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";
import React, { Component } from "react";

interface Props {
    workerSrc?: string;
    url: string;
    beforeLoad: JSX.Element;
    errorMessage?: JSX.Element;
    children: (pdfDocument: PDFDocumentProxy) => JSX.Element;
    onError?: (error: Error) => void;
    cMapUrl?: string;
    cMapPacked?: boolean;
}

interface State {
    pdfDocument: PDFDocumentProxy | null;
    error: Error | null;
}

export class PdfLoader extends Component<Props, State> {
    documentRef = React.createRef<HTMLElement>();

    constructor(props: Props) {
        super(props);
        this.state = {
            pdfDocument: null,
            error: null,
        };
    }

    componentDidMount() {
        this.load();
    }

    componentDidUpdate(prevProps: Props) {
        const { url } = this.props;
        if (prevProps.url !== url) {
            this.load();
        }
    }

    componentDidCatch(error: Error) {
        const { onError } = this.props;

        if (onError) {
            onError(error);
        }

        this.setState({ pdfDocument: null, error });
    }

    componentWillUnmount() {
        const { pdfDocument: discardedDocument } = this.state;
        if (discardedDocument) {
            discardedDocument.destroy();
        }
    }

    load() {
        const { ownerDocument = document } = this.documentRef.current || {};
        const { url, cMapUrl, cMapPacked, workerSrc } = this.props;
        const { pdfDocument: discardedDocument } = this.state;
        this.setState({ pdfDocument: null, error: null });
        if (typeof workerSrc === "string") {
            GlobalWorkerOptions.workerSrc = workerSrc;
        }

        Promise.resolve()
            .then(() => discardedDocument?.destroy())
            .then(() => {
                if (!url) {
                    return undefined;
                }

                const document = {
                    ...this.props,
                    ownerDocument,
                    cMapUrl,
                    cMapPacked,
                };

                return getDocument(document).promise.then((pdfDocument) => {
                    this.setState({ pdfDocument });
                });
            })
            .catch((e) => this.componentDidCatch(e));
    }

    renderError() {
        const { errorMessage } = this.props;
        const { error } = this.state;
        if (errorMessage) {
            return React.cloneElement(errorMessage, { error });
        }

        return null;
    }

    render() {
        const { children, beforeLoad } = this.props;
        const { pdfDocument, error } = this.state;
        return (
            <>
                <span ref={this.documentRef} />
                {error
                    ? this.renderError()
                    : !pdfDocument || !children
                        ? beforeLoad
                        : children(pdfDocument)}
            </>
        );
    }
}

(PdfLoader as React.ComponentType<Props>).defaultProps = {
    workerSrc: "https://unpkg.com/pdfjs-dist@4.4.168/build/pdf.worker.min.mjs",
};